/**
 * Interfejs opisujący pojedynczą zmienną szablonu umowy sprzedaży.
 * @since 17.04.2026
 */
export interface SalesAgreementVariable {
  /** Klucz szablonu w postaci {{nazwaKlucza}} */
  key: string;
  /** Etykieta wyświetlana na przycisku i w formularzu dokumentu */
  label: string;
  /** Podpowiedź (tooltip) opisująca zastosowanie zmiennej */
  tooltip: string;
  /** Grupa logiczna pola używana do sekcji formularza */
  group: "buyer" | "seller" | "other";

  /**
   * Gdy true, pole wymaga osobnej wartości tłumaczenia dla drugiego języka
   * w trybie dokumentu dwujęzycznego.
   */
  requiredTranslation?: boolean;

  value?: string | number; // opcjonalna wartość domyślna dla zmiennych bez orderPath

  /**
   * Informuje, że pole powinno być językowo-zależne (oddzielna wartość na język).
   * Gdy true, przy kopiowaniu danych między językami wartość nie jest automatycznie przepisywana.
   */
  flexible?: boolean;
  /**
   * Gdy true, pole jest zablokowane do edycji w formularzu.
   * Używane np. dla pola podpisu, które zawsze wyświetla grafikę.
   */
  disabled?: boolean;
  /**
   * Kod uprawnienia, które (oprócz superadmin) pozwala edytować pole oznaczone jako disabled.
   */
  editPermission?: string;
  /**
   * Gdy true, pole jest ukryte w formularzu, ale nadal może być używane w szablonie.
   * Używane np. dla pola podpisu, które jest zastępowane grafiką przy druku.
   */
  hidden?: boolean;
  /**
   * Gdy true, pole zawsze pochodzi z danych zamówienia (Order)
   * i nie powinno być zapisywane w fieldValues rekordu dokumentu.
   */
  orderDerived?: boolean;
  /**
   * Ścieżka dot-notation do pola w obiekcie zamówienia.
   * Gdy pole wymaga obliczenia (formatter: 'computed'), ścieżka wskazuje
   * na główne pole źródłowe – logika obliczeniowa w resolveVariableValue().
   */
  orderPath?: string;
  /**
   * Sposób formatowania wartości pobranej z zamówienia:
   * - 'date'     → format DD.MM.YYYY
   * - 'currency' → kwota z walutą
   * - 'computed' → wartość obliczana przez dedykowaną funkcję
   * - 'text'     → bez transformacji (wartość zwracana as-is)
   */
  formatter?: "date" | "currency" | "text" | "computed";
  /**
   * Gdy true, przy renderowaniu szablonu wartość pola jest formatowana jako kwota
   * z walutą z kontrolki {{currency}}.
   */
  renderAsCurrency?: boolean;
  /** Typ pola formularza w SalesAgreementDocumentComponent */
  inputType?: "text" | "number" | "date" | "textarea" | "select";
  /** Lista opcji dla pól typu select */
  selectOptions?: Array<{ value: string; label: string }>;
}

/**
 * Lista zmiennych szablonu umowy sprzedaży wraz z mapowaniem
 * na odpowiednie pola obiektu zamówienia (Order).
 *
 * Kolumna orderPath: ścieżka dot-notation w strukturze zamówienia zwracanego
 * przez GET /orders/:id (np. finances.prices.priceNetto).
 *
 * Zmienne bez orderPath muszą być uzupełniane ręcznie przez użytkownika
 * w formularzu SalesAgreementDocumentComponent.
 *
 * @since 17.04.2026
 */
export const SALES_AGREEMENT_VARIABLES: SalesAgreementVariable[] = [
  {
    key: "{{date}}",
    label: "Data",
    tooltip: "",
    group: "other",
    formatter: "date",
    orderPath: "orderTitle.date",
    inputType: "date",
  },
  {
    key: "{{podpis}}",
    label: "Podpis",
    tooltip: "",
    group: "seller",
    inputType: "text",
    hidden: true,
  },
  {
    key: "{{buyerCompanyName}}",
    label: "Nazwa firmy",
    tooltip: "",
    group: "buyer",
    // Order.companyName – pełna nazwa firmy kupującego
    orderPath: "companyName",
    formatter: "text",
    inputType: "text",
  },
  {
    key: "{{buyerRegisteredStreet}}",
    label: "Ulica",
    tooltip: "",
    group: "buyer",
    // Brak dedykowanego pola w Order – uzupełnienie ręczne
    inputType: "text",
  },
  {
    key: "{{buyerRegisteredBuildingNumber}}",
    label: "Nr budynku",
    tooltip: "",
    group: "buyer",
    // Brak dedykowanego pola w Order – uzupełnienie ręczne
    inputType: "text",
  },
  {
    key: "{{buyerRegisteredUnit}}",
    label: "Nr lokalu",
    tooltip: "",
    group: "buyer",
    // Brak dedykowanego pola w Order – uzupełnienie ręczne
    inputType: "text",
  },
  {
    key: "{{buyerRegisteredPostalCode}}",
    label: "Kod pocztowy",
    tooltip: "",
    group: "buyer",
    // Brak dedykowanego pola w Order – uzupełnienie ręczne
    inputType: "text",
  },
  {
    key: "{{buyerRegisteredCity}}",
    label: "Miejscowość",
    tooltip: "",
    group: "buyer",
    // Brak dedykowanego pola w Order – uzupełnienie ręczne
    inputType: "text",
  },
  {
    key: "{{buyerRegisteredCountry}}",
    label: "Kraj",
    tooltip: "",
    group: "buyer",
    orderDerived: true,
    orderPath: "finances.costs.market",
    inputType: "text",
    disabled: true,
    requiredTranslation: true,
  },
  {
    key: "{{buyerTaxId}}",
    label: "NIP / Tax ID",
    tooltip: "",
    group: "buyer",
    // Brak dedykowanego pola w Order – uzupełnienie ręczne
    inputType: "text",
  },
  {
    key: "{{buyerEuTaxIdVerified}}",
    label: "NIP EU zweryfikowany",
    tooltip: "",
    group: "buyer",
    inputType: "select",
    selectOptions: [
      { value: "TAK", label: "Tak" },
      { value: "NIE", label: "Nie" },
    ],
  },
  {
    key: "{{buyerRepresentativeFullName}}",
    label: "Reprezentant (imię i nazwisko)",
    tooltip: "",
    group: "buyer",
    // Brak dedykowanego pola w Order – uzupełnienie ręczne
    inputType: "text",
  },
  {
    key: "{{companyEmail}}",
    label: "Email kupującego",
    tooltip: "Wstaw email kupującego",
    group: "buyer",
    // Order.emailAddress – adres email klienta
    orderPath: "emailAddress",
    formatter: "text",
    inputType: "text",
  },
  {
    key: "{{priceNetto}}",
    label: "Cena sprzedaży netto",
    tooltip: "",
    group: "other",
    orderDerived: true,
    // Order.finances.prices.priceNetto pomniejszone o Order.finances.prices.discountNetto (jeśli podano)
    orderPath: "finances.prices.priceNetto",
    formatter: "computed",
    renderAsCurrency: true,
    inputType: "number",
    disabled: true,
  },
  {
    key: "{{priceNettoDescription}}",
    label: "Opis ceny sprzedaży",
    tooltip: "np. sto dziewięćdziesiąt pięć tysięcy euro",
    group: "other",
    requiredTranslation: true,
    inputType: "text",
  },
  {
    key: "{{currency}}",
    label: "Waluta",
    tooltip: "",
    group: "other",
    orderDerived: true,
    // Order.finances.prices.currencyChoice – symbol waluty (np. EUR, PLN)
    orderPath: "finances.prices.currencyChoice",
    formatter: "text",
    inputType: "select",
    selectOptions: [
      { value: "EUR", label: "EUR" },
      { value: "PLN", label: "PLN" },
    ],
    disabled: true,
  },
  {
    key: "{{advancePayments}}",
    label: "Wartość zaliczek",
    tooltip: "",
    group: "other",
    orderDerived: true,
    // Order.finances.advancePayments[] – tablica zaliczek; wartość obliczana jako suma payment.value
    orderPath: "finances.advancePayments",
    formatter: "computed",
    renderAsCurrency: true,
    inputType: "number",
    disabled: true,
    editPermission: "UPDATE_SALES_AGREEMENT_ADVANCE_PAYMENTS",
  },
  {
    key: "{{advancePaymentsBrutto}}",
    label: "Wartość zaliczek brutto",
    tooltip: "",
    group: "other",
    formatter: "computed",
    renderAsCurrency: true,
    inputType: "number",
    disabled: true,
    editPermission: "UPDATE_SALES_AGREEMENT_ADVANCE_PAYMENTS",
  },
  {
    key: "{{deliveryDate}}",
    label: "Termin wydania pojazdu",
    tooltip: "",
    group: "other",
    orderDerived: true,
    // Order.deadlineDate – planowana data dostarczenia pojazdu
    orderPath: "deadlineDate",
    formatter: "date",
    inputType: "date",
    disabled: true,
  },
  {
    key: "{{representative}}",
    label: "Reprezentant",
    tooltip: "",
    group: "seller",
    value: "Marek Truszkowski",
    // Brak dedykowanego pola w Order – uzupełnienie ręczne
    inputType: "text",
    disabled: true,
  },
  {
    key: "{{sellerPartnership}}",
    label: "Spółka",
    tooltip: "",
    group: "seller",
    value: `Auto-CUBY Spółka z ograniczoną odpowiedzialnością`,
    inputType: "select",
    selectOptions: [
      {
        value: `Auto-CUBY Spółka z ograniczoną odpowiedzialnością`,
        label: "Auto-CUBY Spółka z ograniczoną odpowiedzialnością",
      },
      {
        value: "Auto-CUBY Sp. z o.o. Spółka Komandytowa",
        label: "Auto-CUBY Sp. z o.o. Spółka Komandytowa",
      },
      {
        value: "M&M Cuby Bus Sp. z o.o.",
        label: "M&M Cuby Bus Sp. z o.o.",
      },
    ],
  },
  {
    key: "{{sellerKrsNip}}",
    label: "KRS i NIP",
    tooltip: "Automatycznie ustawiane na podstawie wyboru spółki",
    group: "seller",
    formatter: "computed",
    inputType: "text",
    hidden: true,
  },
  {
    key: "{{orderManager}}",
    label: "Twórca zamówienia (imię i nazwisko)",
    tooltip: "",
    group: "seller",
    orderPath: "creator.firstName",
    formatter: "computed",
    inputType: "text",
  },
  {
    key: "{{creatorEmail}}",
    label: "Email twórcy / opiekuna zamówienia",
    tooltip: "",
    group: "seller",
    orderPath: "creator.email",
    formatter: "text",
    inputType: "text",
  },
  {
    key: "{{vehicleData}}",
    label: "Dane dot. pojazdu",
    tooltip: "",
    group: "other",
    orderPath: "conversion.name",
    formatter: "computed",
    inputType: "text",
    orderDerived: true,
    disabled: true,
  },
  {
    key: "{{deliveryLocation}}",
    label: "Miejsce dostarczenia pojazdu",
    tooltip: "",
    group: "other",
    value: "Gościcino, ul. Przemysłowa 2",
    // Brak dedykowanego pola w Order – uzupełnienie ręczne
    inputType: "text",
    disabled: true,
  },
  {
    key: "{{specificationNumber}}",
    label: "Numer specyfikacji",
    tooltip: "",
    group: "other",
    orderDerived: true,
    orderPath: "orderTitle.count",
    formatter: "computed",
    inputType: "text",
    disabled: true,
  },
];
